import { useState } from 'react';
import {
  Type,
  Square,
  Image,
  Minus,
  PenTool,
  Layers,
  ChevronDown,
  ChevronRight,
  MousePointerClick,
  FolderOpen,
} from 'lucide-react';
import { useCanvasStore, type EditorTool } from '../../stores/canvasStore';
import { useStudioStore } from '../../stores/studioStore';
import { useAssetStore } from '../../stores/assetStore';

const QUICK_INSERT = [
  { id: 'text' as EditorTool, icon: Type, label: 'Text', shortcut: 'T' },
  { id: 'shape' as EditorTool, icon: Square, label: 'Shape', shortcut: 'R' },
  { id: 'image' as EditorTool, icon: Image, label: 'Image', shortcut: 'M' },
  { id: 'line' as EditorTool, icon: Minus, label: 'Line', shortcut: 'L' },
  { id: 'pen' as EditorTool, icon: PenTool, label: 'Path', shortcut: 'P' },
  { id: 'button' as EditorTool, icon: MousePointerClick, label: 'Button', shortcut: 'B' },
];

interface NoSelectionPanelProps {
  artboardSettingsContent: React.ReactNode;
}

export default function NoSelectionPanel({ artboardSettingsContent }: NoSelectionPanelProps) {
  const { setActiveTool } = useCanvasStore();
  const { canvasState } = useStudioStore();
  const { assets } = useAssetStore();
  const [showArtboard, setShowArtboard] = useState(false);

  const elements = canvasState.elements;
  const recentAssets = assets.slice(0, 6);
  const bgCount = elements.filter((e) => e.layer === 'BACKGROUND_LAYER').length;
  const ovCount = elements.filter((e) => e.layer === 'OVERLAY_LAYER').length;

  return (
    <div className="flex-1 overflow-y-auto sidebar-scroll">
      <div className="p-4 space-y-5">
        <div>
          <h4 className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-2.5">
            Quick Insert
          </h4>
          <div className="grid grid-cols-2 gap-1.5">
            {QUICK_INSERT.map((tool) => {
              const Icon = tool.icon;
              return (
                <button
                  key={tool.id}
                  onClick={() => setActiveTool(tool.id)}
                  className="
                    flex items-center gap-2 px-2.5 py-2
                    bg-white/[0.03] hover:bg-white/[0.07] border border-white/[0.06]
                    rounded-lg transition-colors
                    text-[11px] text-zinc-400 hover:text-zinc-200
                  "
                >
                  <Icon size={14} className="text-cyan-400/70" />
                  <span>{tool.label}</span>
                  <span className="ml-auto text-[9px] text-zinc-600">{tool.shortcut}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="border-t border-white/[0.04] pt-4">
          <h4 className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-2.5">
            Canvas Summary
          </h4>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between px-2 py-1.5 bg-white/[0.02] rounded-md">
              <div className="flex items-center gap-2">
                <Layers size={12} className="text-zinc-600" />
                <span className="text-[11px] text-zinc-400">Total Elements</span>
              </div>
              <span className="text-[11px] text-zinc-300 font-mono">{elements.length}</span>
            </div>
            <div className="flex items-center justify-between px-2 py-1.5 bg-white/[0.02] rounded-md">
              <span className="text-[11px] text-zinc-500 pl-6">Background</span>
              <span className="text-[11px] text-zinc-400 font-mono">{bgCount}</span>
            </div>
            <div className="flex items-center justify-between px-2 py-1.5 bg-white/[0.02] rounded-md">
              <span className="text-[11px] text-zinc-500 pl-6">Overlay</span>
              <span className="text-[11px] text-zinc-400 font-mono">{ovCount}</span>
            </div>
          </div>

          {elements.length > 0 && (
            <div className="mt-3 space-y-1">
              {elements.slice(0, 5).map((el) => (
                <div
                  key={el.id}
                  className="flex items-center gap-2 px-2 py-1 rounded text-[11px] text-zinc-500"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 shrink-0" />
                  <span className="truncate">{el.name || el.type}</span>
                  <span className="ml-auto text-[9px] text-zinc-700 capitalize">{el.type}</span>
                </div>
              ))}
              {elements.length > 5 && (
                <p className="text-[10px] text-zinc-600 px-2 pt-0.5">
                  +{elements.length - 5} more
                </p>
              )}
            </div>
          )}
        </div>

        {recentAssets.length > 0 && (
          <div className="border-t border-white/[0.04] pt-4">
            <h4 className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-2.5">
              Recent Assets
            </h4>
            <div className="grid grid-cols-3 gap-1.5">
              {recentAssets.map((asset) => (
                <div
                  key={asset.id}
                  className="
                    aspect-square bg-white/[0.03] rounded-lg overflow-hidden
                    hover:ring-1 hover:ring-cyan-500/40 cursor-pointer
                    transition-all border border-white/[0.06]
                  "
                  title={asset.name}
                >
                  {asset.mime_type?.startsWith('image/') && asset.thumbnail_url ? (
                    <img
                      src={asset.thumbnail_url}
                      alt={asset.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FolderOpen size={16} className="text-zinc-600" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="border-t border-white/[0.04] pt-4">
          <button
            onClick={() => setShowArtboard(!showArtboard)}
            className="
              flex items-center justify-between w-full
              text-[10px] font-semibold text-zinc-500 uppercase tracking-wider
              hover:text-zinc-400 transition-colors
            "
          >
            <span>Artboard Settings</span>
            {showArtboard ? (
              <ChevronDown size={12} className="text-zinc-600" />
            ) : (
              <ChevronRight size={12} className="text-zinc-600" />
            )}
          </button>

          {showArtboard && (
            <div className="mt-3">
              {artboardSettingsContent}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
